import { MediaRender } from '@components/MediaRender'
import { Slider } from '@components/Slider'
import { ILesson, IMedia } from '@dtos/Course'
import { useDynamicSnapPoints } from '@hooks/useDynamicSnapPoints'
import { GestureReponderEvent, isWeb, useMedia } from '@tamagui/core'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useEffect, useMemo, useState } from 'react'
import { useWindowDimensions } from 'react-native'
import RenderHtml from 'react-native-render-html'
import { Text, View, XStack, YStack, useTheme } from 'tamagui'
import { ExtraContentCard } from '../extra_contents/ExtraContentCard'
import { ExtraContentModal } from '../extra_contents/ExtraContentModal'
import { NotSupported } from '../extra_contents/NotSupported'

import { HeroTabs } from '@components/HeroTabs'
import { useLessonTabStore } from '@stores/useLessonTabStore'

interface IDisableBtns {
  isFirstContent: boolean
  isLastContent: boolean
}

interface IModalExtraMediaProps extends IMedia {
  index?: number | null
  isMain?: boolean
  disableBtns?: IDisableBtns
}

interface ITabsContentContainerProps {
  currentLesson: ILesson | null
  withoutTabContent?: boolean
  withoutTabs?: boolean
  currentTab?: string
  onTabChange?: (tab: string) => void
}

export function TabsContentContainer({
  currentLesson,
  currentTab,
  onTabChange,
  withoutTabContent,
  withoutTabs,
}: ITabsContentContainerProps) {
  const { width } = useWindowDimensions()
  const media = useMedia()
  const theme = useTheme()
  const [openExtraContentModal, setOpenExtraContentModal] = useState(false)
  const [extraMedia, setExtraMedia] = useState<IModalExtraMediaProps | null>(
    null,
  )

  const { setTabs } = useLessonTabStore()

  const cardWidth = useMemo(() => {
    if (media.gtMd && !media.gtLg) return 324
    if (!media.gtLg) return 407
    return 242
  }, [media])

  const perView = Math.max(1, Math.floor(width / (cardWidth * 1.1)))
  const totalSlides = currentLesson?.extraMedias?.length ?? 0
  const hasExtraContent = Boolean(
    currentLesson && currentLesson?.extraMedias?.length > 0,
  )
  const hasDescription = Boolean(currentLesson?.description)
  const isPDF = extraMedia?.fileExt?.toLowerCase() === 'pdf'
  const mediaType = isPDF ? 'pdf' : extraMedia?.type || 'Text'

  const handlePressDownload = (
    e: GestureReponderEvent,
    link: string | null | undefined,
  ) => {
    e.stopPropagation()
    if (link) {
      openWebBrowser(link)
    }
  }

  const setExtraContentModal = (media: IModalExtraMediaProps) => {
    setExtraMedia(media)
    setOpenExtraContentModal(true)
  }

  const changePreviewContent = (i: number) => {
    if (!currentLesson?.extraMedias) return
    if (
      extraMedia?.index !== undefined &&
      extraMedia?.index !== null &&
      extraMedia?.index + i >= 0
    ) {
      if (extraMedia?.isMain) {
        if (extraMedia?.index + i < currentLesson.extraMedias.length) {
          const extraContent = {
            ...currentLesson.extraMedias[extraMedia?.index + i],
            index: extraMedia?.index + i,
          }
          setExtraMedia({
            ...extraContent,
            index: extraMedia?.index + i,
            isMain: true,
            disableBtns: {
              isFirstContent: extraMedia?.index + i === 0,
              isLastContent:
                extraMedia?.index + i === currentLesson.extraMedias.length - 1,
            },
          })
        }
      }
    }
  }

  const textColor = useMemo(() => {
    try {
      return theme['$neutral-800']?.get() || theme.$color?.get() || '#262626'
    } catch {
      return '#262626'
    }
  }, [theme])

  const descriptionContent = useMemo(() => {
    return (
      <View
        h="100%"
        paddingHorizontal="$spacing-inset-xs"
        paddingVertical="$spacing-inset-md"
        paddingBottom={!isWeb ? 300 : 0}
      >
        {currentLesson?.description ? (
          <RenderHtml
            contentWidth={width}
            source={{ html: currentLesson.description }}
            baseStyle={{
              color: textColor,
              fontSize: 12,
              fontFamily: 'Mulish-Regular',
            }}
          />
        ) : (
          <Text
            color="$neutral-800"
            fontFamily="$mulishRegular"
            fontSize="$font-size-sm"
          >
            Sem descrição disponível
          </Text>
        )}
      </View>
    )
  }, [currentLesson?.description, textColor, width])

  const complementaryContent = useMemo(() => {
    const handlePressOpenModal = (
      media: IMedia,
      index: number,
      isMain: boolean,
      nodes: number,
    ) => {
      const mediaToOpen = {
        ...media,
        id: media.id,
        type: media.type || null,
        fileUrl: media.fileUrl || null,
        fileExt: media.fileExt || null,
        text: media.text || null,
        title: media.title,
        index,
        isMain,
        disableBtns: {
          isFirstContent: index === 0,
          isLastContent: index === nodes - 1,
        },
      }

      setExtraContentModal(mediaToOpen)
    }

    return (
      <YStack
        paddingLeft="$spacing-inset-xs"
        overflow="hidden"
        paddingBottom={!isWeb ? 220 : 0}
      >
        <Slider
          slidesTotalNumber={totalSlides}
          slidesPerView={perView}
          displaySliderControls={totalSlides > perView}
          slidesSpacing={16}
          sliderTitle={
            <XStack
              backgroundColor="$info-300"
              paddingHorizontal="$spacing-inset-xxs"
              paddingVertical="$spacing-inset-quarck"
              borderRadius="$border-radius-pill"
              marginVertical="$spacing-inset-sm"
            >
              <Text
                color="$neutral-0"
                fontFamily="$mulishSemiBold"
                fontWeight="600"
                fontSize={14}
              >
                {totalSlides} conteúdos
              </Text>
            </XStack>
          }
        >
          {currentLesson?.extraMedias?.map((content, index) => (
            <ExtraContentCard
              key={content.id}
              extraContent={{
                id: content.id,
                title: content.title,
                media: {
                  id: content.id,
                  title: content.title,
                  type: content.type,
                  duration: null,
                  fileUrl: content.fileUrl,
                  fileExt: content.fileExt,
                  text: content.text,
                  hostKey: content.hostKey,
                  host: content.host,
                  organizationUuid: content.organizationUuid,
                },
                downloadable: !!content.fileUrl || !!content.fileExt,
                releaseAt: currentLesson?.releaseAt,
                releaseAfter: currentLesson?.releaseAfter,
                createdAt: currentLesson?.createdAt,
              }}
              isLessonAvailable={true}
              width={cardWidth}
              minHeight={210}
              handlePressDownload={handlePressDownload}
              handlePressOpenModal={() =>
                handlePressOpenModal(
                  {
                    id: content.id,
                    title: content.title,
                    type: content.type,
                    duration: null,
                    fileUrl: content.fileUrl,
                    fileExt: content.fileExt,
                    text: content.text,
                    hostKey: content.hostKey,
                    host: content.host,
                    organizationUuid: content.organizationUuid,
                  },
                  index,
                  true,
                  currentLesson.extraMedias.length,
                )
              }
            />
          ))}
        </Slider>
      </YStack>
    )
  }, [
    cardWidth,
    currentLesson?.createdAt,
    currentLesson?.extraMedias,
    currentLesson?.releaseAfter,
    currentLesson?.releaseAt,
    perView,
    totalSlides,
  ])

  const tabs = useMemo(
    () => [
      {
        label: 'Descrição',
        value: 'description',
        content: descriptionContent,
        defaultActive: hasDescription,
        isDisabled: !hasDescription,
      },
      {
        label: 'Conteúdo Complementar',
        value: 'complementary',
        content: complementaryContent,
        defaultActive: !hasDescription && hasExtraContent,
        isDisabled: !hasExtraContent,
      },
    ],
    [hasDescription, hasExtraContent, descriptionContent, complementaryContent],
  )

  useEffect(() => {
    setTabs(tabs)
  }, [tabs, setTabs])

  return (
    <>
      <HeroTabs
        key={currentLesson?.id?.toString() || ''}
        orientation="horizontal"
        tabs={tabs}
        withBorderTop={true}
        borderBottomRadius={
          media.gtSm ? '$border-radius-xlg' : '$border-radius-none'
        }
        onTabChange={onTabChange}
        currentTab={currentTab}
        withoutTabs={withoutTabs}
        withoutTabContent={withoutTabContent}
      />

      <ExtraContentModal
        mediaType={mediaType}
        open={openExtraContentModal}
        onClose={() => setOpenExtraContentModal(false)}
        title={extraMedia?.title || ''}
        onChangeContent={(i: number) => changePreviewContent(i)}
        disabledBtns={extraMedia?.disableBtns}
        snapPoints={useDynamicSnapPoints().snapPoints}
        shouldAdapt={false}
        zIndex={100000}
      >
        {extraMedia && (
          <MediaRender media={extraMedia}>
            <NotSupported media={extraMedia} />
          </MediaRender>
        )}
      </ExtraContentModal>
    </>
  )
}
